<script lang="ts" setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { DateTime } from 'luxon'
import { formatBytes } from '@/utils'
import { AdminRegistryDetails } from '@/types/admin'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AdminRegistryDetail' })

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const notifyStore = useNotifyStore()

const loading = ref(true)
const registry = ref<AdminRegistryDetails | null>(null)
const actionId = ref<number | null>(null)
const confirmAction = ref<{ id: number; type: 'suspend' | 'unsuspend' } | null>(null)

const registryId = computed(() => Number(route.params.id))
const pageTitle = computed(() => registry.value?.slug || 'Registry Details')
const pageSubtitle = computed(() => `Registry #${registryId.value}`)

const latestUsage = computed(() => registry.value?.usage_records?.[0] ?? null)

function formatDate(dateString: string | null | undefined): string {
  if (!dateString) return '-'
  return DateTime.fromISO(dateString).toFormat('dd.MM.yyyy HH:mm')
}

function getStatusClass(status: string): string {
  switch (status) {
    case 'active': return 'status-active'
    case 'suspended': return 'status-suspended'
    case 'deactivated': return 'status-deactivated'
    case 'quota_exceeded': return 'status-quota-exceeded'
    default: return ''
  }
}

function statusLabel(status: string): string {
  const key = `dashboard.admin.registry.statuses.${status}`
  const label = t(key)
  return label === key ? status : label
}

async function fetchRegistry() {
  loading.value = true
  try {
    const res = await useAxios<null, AdminRegistryDetails>(
      'GET',
      `/admin/registry/${registryId.value}`,
      true
    )
    if (res && res.status === STATUS_CODES.OK && res.data) {
      const validate = AdminRegistryDetails.safeParse(res.data)
      if (validate.success) {
        registry.value = validate.data
      } else {
        registry.value = res.data as AdminRegistryDetails
      }
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: 'AdminRegistryDetail' })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: 'Fehler',
      message: getErrorMessage(err as Error)
    })
  } finally {
    loading.value = false
  }
}

function promptAction(type: 'suspend' | 'unsuspend') {
  if (!registry.value) return
  confirmAction.value = { id: registry.value.id, type }
}

function cancelAction() {
  confirmAction.value = null
}

async function executeAction() {
  if (!confirmAction.value || !registry.value) return
  const { id, type } = confirmAction.value

  confirmAction.value = null
  actionId.value = id

  try {
    const res = await useAxios<null, null>('POST', `/admin/registry/${id}/${type}`, true)
    if (res && res.status === STATUS_CODES.NO_CONTENT) {
      registry.value.status = type === 'suspend' ? 'suspended' : 'active'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'success',
        title: t(`dashboard.admin.registry.actions.${type}Success`),
        message: registry.value.slug || ''
      })
    }
  } catch (err) {
    handleAxiosError(err as Error, { logPrefix: `AdminRegistryDetail:${type}` })
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t(`dashboard.admin.registry.actions.${type}`),
      message: getErrorMessage(err as Error)
    })
  } finally {
    actionId.value = null
  }
}

onMounted(() => {
  fetchRegistry()
})
</script>

<template>
  <BaseDashboardPage
    :title="pageTitle"
    :subtitle="pageSubtitle"
    icon="mdi-docker"
    icon-color="teal"
    show-back
    :back-route="{ name: 'dashboardAdminRegistry' }"
  >
    <template #actions>
      <button
        v-if="registry?.status === 'active'"
        class="action-btn suspend-btn"
        :disabled="actionId !== null"
        @click="promptAction('suspend')"
      >
        <v-icon icon="mdi-lock" size="16" />
        {{ t('dashboard.admin.registry.actions.suspend') }}
      </button>
      <button
        v-else-if="registry?.status === 'suspended'"
        class="action-btn unsuspend-btn"
        :disabled="actionId !== null"
        @click="promptAction('unsuspend')"
      >
        <v-icon icon="mdi-lock-open" size="16" />
        {{ t('dashboard.admin.registry.actions.unsuspend') }}
      </button>
      <v-btn
        icon
        variant="text"
        :loading="loading"
        @click="fetchRegistry"
      >
        <v-icon icon="mdi-refresh" />
      </v-btn>
    </template>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <v-progress-circular indeterminate size="48" color="primary" />
    </div>

    <!-- Content -->
    <div v-else-if="registry" class="detail-content">
      <!-- Info Cards -->
      <div class="info-grid">
        <!-- Registry Info -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.registry.detail.registryInfo') }}</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.status') }}</span>
              <span class="status-badge" :class="getStatusClass(registry.status)">
                {{ statusLabel(registry.status) }}
              </span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.region') }}</span>
              <span class="region-chip">{{ registry.region }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.shard') }}</span>
              <span class="info-value mono">{{ registry.shard }}</span>
            </div>
            <div v-if="registry.slug" class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.slug') }}</span>
              <span class="info-value">{{ registry.slug }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.storageQuota') }}</span>
              <span class="info-value mono">{{ formatBytes(registry.storage_quota_bytes) }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.created') }}</span>
              <span class="info-value">{{ formatDate(registry.created_at) }}</span>
            </div>
          </div>
        </div>

        <!-- Account -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.registry.detail.accountInfo') }}</h3>
          <div class="info-rows">
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.accountEmail') }}</span>
              <span class="info-value">{{ registry.account_email }}</span>
            </div>
            <div class="info-row">
              <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.accountId') }}</span>
              <span
                class="info-value account-link"
                @click="router.push({ name: 'dashboardAdminUserDetail', params: { id: registry.account_id } })"
              >
                #{{ registry.account_id }}
              </span>
            </div>
          </div>
        </div>

        <!-- Billing / Stripe -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.registry.detail.billing') }}</h3>
          <div class="info-rows">
            <template v-if="registry.stripe_subscription_id || registry.stripe_storage_item_id || registry.stripe_egress_item_id">
              <div v-if="registry.stripe_subscription_id" class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.stripeSubscription') }}</span>
                <span class="info-value mono stripe-id">{{ registry.stripe_subscription_id }}</span>
              </div>
              <div v-if="registry.stripe_storage_item_id" class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.stripeStorageItem') }}</span>
                <span class="info-value mono stripe-id">{{ registry.stripe_storage_item_id }}</span>
              </div>
              <div v-if="registry.stripe_egress_item_id" class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.stripeEgressItem') }}</span>
                <span class="info-value mono stripe-id">{{ registry.stripe_egress_item_id }}</span>
              </div>
            </template>
            <div v-else class="empty-hint">
              {{ t('dashboard.admin.registry.detail.noBilling') }}
            </div>
          </div>
        </div>

        <!-- Latest Usage -->
        <div class="info-card">
          <h3 class="card-title">{{ t('dashboard.admin.registry.detail.usage') }}</h3>
          <div class="info-rows">
            <template v-if="latestUsage">
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.storageUsed') }}</span>
                <span class="info-value mono">{{ formatBytes(latestUsage.storage_bytes) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.egressUsed') }}</span>
                <span class="info-value mono">{{ formatBytes(latestUsage.egress_bytes) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.pullCount') }}</span>
                <span class="info-value">{{ latestUsage.pull_count.toLocaleString() }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.pushCount') }}</span>
                <span class="info-value">{{ latestUsage.push_count.toLocaleString() }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.period') }}</span>
                <span class="info-value">{{ formatDate(latestUsage.period_start) }} – {{ formatDate(latestUsage.period_end) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ t('dashboard.admin.registry.detail.fields.reportedToStripe') }}</span>
                <span class="info-value">
                  <v-icon
                    :icon="latestUsage.reported_to_stripe ? 'mdi-check-circle' : 'mdi-close-circle'"
                    :color="latestUsage.reported_to_stripe ? 'success' : 'warning'"
                    size="18"
                  />
                </span>
              </div>
            </template>
            <div v-else class="empty-hint">
              {{ t('dashboard.admin.registry.detail.noUsage') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Tokens Section -->
      <div class="section">
        <h2 class="section-title">{{ t('dashboard.admin.registry.detail.tokens') }}</h2>
        <div v-if="registry.tokens?.length" class="table-container">
          <table class="registry-table">
            <thead>
              <tr>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.name') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.prefix') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.active') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.scopes') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.created') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.lastUsed') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.tokenColumns.expires') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="token in registry.tokens" :key="token.id" class="table-row">
                <td class="name-cell">{{ token.name }}</td>
                <td class="mono">{{ token.token_prefix }}</td>
                <td>
                  <v-icon
                    :icon="token.active ? 'mdi-check-circle' : 'mdi-close-circle'"
                    :color="token.active ? 'success' : 'error'"
                    size="18"
                  />
                </td>
                <td>
                  <span
                    v-for="scope in (token.scopes || [])"
                    :key="scope"
                    class="scope-chip"
                  >{{ scope }}</span>
                  <span v-if="token.pull_only" class="type-chip">{{ t('dashboard.admin.registry.detail.pullOnly') }}</span>
                  <span v-if="!token.scopes?.length && !token.pull_only" class="empty-hint">-</span>
                </td>
                <td class="date-cell">{{ formatDate(token.created_at) }}</td>
                <td class="date-cell">{{ formatDate(token.last_used) }}</td>
                <td class="date-cell">{{ formatDate(token.expires_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-section">
          {{ t('dashboard.admin.registry.detail.noTokens') }}
        </div>
      </div>

      <!-- Cleanup Rules Section -->
      <div class="section">
        <h2 class="section-title">{{ t('dashboard.admin.registry.detail.cleanupRules') }}</h2>
        <div v-if="registry.cleanup_rules?.length" class="table-container">
          <table class="registry-table">
            <thead>
              <tr>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.repository') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.ruleType') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.value') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.keepPatterns') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.deletePatterns') }}</th>
                <th>{{ t('dashboard.admin.registry.detail.ruleColumns.enabled') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="rule in registry.cleanup_rules" :key="rule.id" class="table-row">
                <td class="name-cell mono">{{ rule.repository }}</td>
                <td><span class="type-chip">{{ rule.rule_type }}</span></td>
                <td class="mono">{{ rule.value }}</td>
                <td>
                  <span
                    v-for="p in (rule.keep_patterns || [])"
                    :key="p"
                    class="scope-chip"
                  >{{ p }}</span>
                  <span v-if="!rule.keep_patterns?.length" class="empty-hint">-</span>
                </td>
                <td>
                  <span
                    v-for="p in (rule.delete_patterns || [])"
                    :key="p"
                    class="scope-chip"
                  >{{ p }}</span>
                  <span v-if="!rule.delete_patterns?.length" class="empty-hint">-</span>
                </td>
                <td>
                  <v-icon
                    :icon="rule.enabled ? 'mdi-check-circle' : 'mdi-close-circle'"
                    :color="rule.enabled ? 'success' : 'error'"
                    size="18"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-section">
          {{ t('dashboard.admin.registry.detail.noCleanupRules') }}
        </div>
      </div>
    </div>

    <!-- Not Found State -->
    <div v-else class="empty-state">
      <v-icon icon="mdi-alert-circle-outline" size="64" />
      <h3>{{ t('dashboard.admin.registry.detail.notFound') }}</h3>
      <p>{{ t('dashboard.admin.registry.detail.notFoundDescription') }}</p>
      <button
        class="action-btn back-btn"
        @click="router.push({ name: 'dashboardAdminRegistry' })"
      >
        {{ t('dashboard.admin.registry.detail.backToList') }}
      </button>
    </div>

    <!-- Confirmation Dialog -->
    <div v-if="confirmAction" class="confirm-overlay" @click.self="cancelAction">
      <div class="confirm-dialog">
        <p class="confirm-message">
          {{
            confirmAction.type === 'suspend'
              ? t('dashboard.admin.registry.actions.suspendConfirm')
              : t('dashboard.admin.registry.actions.unsuspendConfirm')
          }}
        </p>
        <div class="confirm-actions">
          <button class="action-btn cancel-btn" @click="cancelAction">
            {{ t('common.cancel') }}
          </button>
          <button
            class="action-btn"
            :class="confirmAction.type === 'suspend' ? 'suspend-btn' : 'unsuspend-btn'"
            @click="executeAction"
          >
            {{ t('common.confirm') }}
          </button>
        </div>
      </div>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
/* Loading & Empty States */
.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state {
  .v-icon {
    color: rgba(120, 163, 255, 0.3);
    margin-bottom: 16px;
  }

  h3 {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 8px;
  }

  p {
    font-size: 0.9rem;
    color: rgba(200, 218, 255, 0.5);
    margin-bottom: 24px;
  }
}

/* Content */
.detail-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

/* Cards */
.info-card {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
}

.card-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 16px;
}

.info-rows {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.info-label {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
}

.info-value {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  text-align: right;
}

.mono {
  font-family: 'JetBrains Mono', monospace;
}

.stripe-id {
  font-size: 0.8rem;
  word-break: break-all;
}

.account-link {
  cursor: pointer;
  color: #64b5f6;

  &:hover {
    text-decoration: underline;
  }
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.35);
  font-style: italic;
}

/* Region Chip */
.region-chip {
  display: inline-flex;
  padding: 3px 10px;
  background: rgba(0, 176, 255, 0.1);
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #64b5f6;
}

/* Status Badges */
.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;

  &.status-active {
    background: rgba(76, 175, 80, 0.15);
    color: #81c784;
  }

  &.status-suspended {
    background: rgba(255, 152, 0, 0.15);
    color: #ffb74d;
  }

  &.status-deactivated {
    background: rgba(158, 158, 158, 0.15);
    color: #bdbdbd;
  }

  &.status-quota-exceeded {
    background: rgba(255, 167, 38, 0.15);
    color: #ffa726;
  }
}

/* Sections */
.section {
  margin-top: 8px;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 16px;
}

.empty-section {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.4);
  font-style: italic;
  padding: 24px;
  text-align: center;
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
}

/* Tables */
.table-container {
  background: rgba(15, 23, 48, 0.5);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.registry-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 14px 16px;
    text-align: left;
  }

  th {
    font-size: 0.75rem;
    font-weight: 600;
    color: rgba(200, 218, 255, 0.5);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    background: rgba(0, 0, 0, 0.15);
    border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  }
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.name-cell {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.date-cell {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.scope-chip,
.type-chip {
  display: inline-flex;
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.1);
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.8);
  margin-right: 4px;
}

.type-chip {
  background: rgba(0, 176, 255, 0.1);
  color: #64b5f6;
}

/* Action Buttons */
.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: none;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.suspend-btn {
  background: rgba(255, 152, 0, 0.15);
  color: #ffb74d;

  &:hover:not(:disabled) {
    background: rgba(255, 152, 0, 0.25);
  }
}

.unsuspend-btn {
  background: rgba(76, 175, 80, 0.15);
  color: #81c784;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
  }
}

.back-btn {
  background: rgba(120, 163, 255, 0.15);
  color: rgba(200, 218, 255, 0.8);

  &:hover {
    background: rgba(120, 163, 255, 0.25);
  }
}

.cancel-btn {
  background: rgba(120, 163, 255, 0.08);
  color: rgba(200, 218, 255, 0.6);

  &:hover {
    background: rgba(120, 163, 255, 0.15);
  }
}

/* Confirmation Dialog */
.confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.confirm-dialog {
  background: rgb(15, 23, 48);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  width: 90%;
}

.confirm-message {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 20px;
  line-height: 1.5;
}

.confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* Light Theme */
.v-theme--lightTheme {
  .info-card,
  .table-container,
  .empty-section {
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .card-title {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-label {
    color: rgba(15, 23, 42, 0.5);
  }

  .info-value {
    color: rgba(15, 23, 42, 0.9);
  }

  .name-cell {
    color: rgba(15, 23, 42, 0.9);
  }

  .date-cell {
    color: rgba(15, 23, 42, 0.6);
  }

  .section-title {
    color: rgba(15, 23, 42, 0.9);
  }

  .empty-hint,
  .empty-section {
    color: rgba(15, 23, 42, 0.4);
  }

  .registry-table th {
    background: rgba(0, 0, 0, 0.02);
    color: rgba(15, 23, 42, 0.5);
  }

  .confirm-dialog {
    background: rgb(255, 255, 255);
    border-color: rgba(28, 110, 227, 0.15);
  }

  .confirm-message {
    color: rgba(15, 23, 42, 0.85);
  }

  .empty-state {
    h3 {
      color: rgba(15, 23, 42, 0.8);
    }

    p {
      color: rgba(15, 23, 42, 0.5);
    }
  }
}

@media (max-width: 768px) {
  .table-container {
    overflow-x: auto;
  }

  .registry-table {
    min-width: 700px;
  }
}
</style>
